/**
 * StrategyDraftReviewPage — access-gated route page that lets the user
 * inspect + edit a draft strategy (B1 output) before approving.
 *
 * URL: /project/:projectId/content-plan/strategy/:strategyId/review
 *
 * Composes:
 *   - useContentStrategyDraft(projectId, strategyId) — real-time onSnapshot
 *   - StrategicAngleCard (one per strategicAngle[])
 *   - SprintDraftBlock (one per sprint)
 *   - StrategyDraftDataUsagePanel (right rail)
 *
 * Persistence:
 *   - Whole-array replacements via patchStrategyDraft({ strategicAngle: [...] })
 *   - Scalar dot-notation via patchStrategyDraft({ "sprints.{id}.audienceRationale": "..." })
 *
 * Approval flow:
 *   - approveStrategy() returns either { success, taskId } (B2 dispatched)
 *     OR HTTP 422 with violations payload (strict policy + post-B2 violations).
 *   - On 422 the user resolves the violations on this same page and retries.
 */

import React, { useCallback, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Stack,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Chip,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuth } from '@frontend/contexts/AuthContext';
import { useContentStrategyDraft } from '@modules/content-plan/hooks/useContentStrategyDraft';
import { useProjectDefaultModel } from '@modules/content-plan/hooks/useProjectDefaultModel';
import { useThemeCatalog } from '@modules/content-plan/hooks/useThemeCatalog';
import { useTopicBacklog } from '@modules/content-plan/hooks/useTopicBacklog';
import { ContentPlanApiClient } from '@modules/content-plan/services/ContentPlanApiClient';
import { AvailableModelSelector } from '@modules/ai-settings';
import { StrategicAngleCard, type StrategicAngleShape } from './StrategicAngleCard';
import { SprintDraftBlock } from './SprintDraftBlock';
import { StrategyDraftDataUsagePanel } from './StrategyDraftDataUsagePanel';
import type {
  AudienceCoverageViolation,
} from '@modules/content-plan/types';
import { canAccessContentPlan, isContentPlanAdminOnly } from '@modules/content-plan/config/access';
import { useProjectCapability } from '@modules/billing-access';
import { CONTENT_PLAN } from '@test-kit/selectors/content-plan';

export const StrategyDraftReviewPage: React.FC = () => {
  const { projectId, strategyId } = useParams<{ projectId: string; strategyId: string }>();
  const { currentUser, isAdmin, adminLoaded } = useAuth() as {
    currentUser: { uid: string } | null;
    isAdmin: boolean;
    adminLoaded: boolean;
  };
  const navigate = useNavigate();
  const urlAuthorId = React.useMemo(() => {
    if (typeof window === 'undefined') return null;
    return new URLSearchParams(window.location.search).get('author');
  }, []);

  const { strategy, sprints, loading, isDraftPending, isApproved, isGenerated } =
    useContentStrategyDraft(projectId ?? null, strategyId ?? null);
  const effectiveAuthorId = ((strategy as any)?.authorId as string | undefined) ?? urlAuthorId ?? '';
  const { themes } = useThemeCatalog(projectId ?? null);
  const { topics: backlog } = useTopicBacklog(projectId ?? null, effectiveAuthorId || null);

  const themeCatalogMap = React.useMemo(() => {
    const m: Record<string, any> = {};
    (themes ?? []).forEach((t) => { m[t.id] = t; });
    return m;
  }, [themes]);

  const apiClient = React.useMemo(() => new ContentPlanApiClient(), []);
  const contentPlanAccess = useProjectCapability(projectId, 'content_plan.access');
  const projectDefaultModel = useProjectDefaultModel(projectId);

  const [savingError, setSavingError] = useState<string | null>(null);
  const [approveError, setApproveError] = useState<string | null>(null);
  const [approveViolations, setApproveViolations] = useState<AudienceCoverageViolation[]>([]);
  const [approving, setApproving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [approvalModel, setApprovalModel] = useState(projectDefaultModel.value);
  const [approvalModelTouched, setApprovalModelTouched] = useState(false);

  React.useEffect(() => {
    setApprovalModelTouched(false);
  }, [strategyId]);

  React.useEffect(() => {
    if (!approvalModelTouched) setApprovalModel(projectDefaultModel.value);
  }, [approvalModelTouched, projectDefaultModel.value]);

  const handleApprovalModelChange = useCallback((model: string) => {
    setApprovalModelTouched(true);
    setApprovalModel(model);
  }, []);

  // ─── persistence helpers ──────────────────────────────────────────
  const patchScalar = useCallback(
    async (path: string, value: unknown) => {
      if (!projectId || !strategyId) return;
      setSavingError(null);
      const res = await apiClient.patchStrategyDraft(strategyId, {
        projectId,
        authorId: effectiveAuthorId,
        patch: { [path]: value },
      });
      if (!res.success) {
        const msg = res.error ?? 'Patch failed';
        setSavingError(msg);
        throw new Error(msg);
      }
    },
    [apiClient, projectId, strategyId, effectiveAuthorId],
  );

  const patchStrategicAngleArray = useCallback(
    async (next: StrategicAngleShape[]) => {
      if (!projectId || !strategyId) return;
      setSavingError(null);
      const res = await apiClient.patchStrategyDraft(strategyId, {
        projectId,
        authorId: effectiveAuthorId,
        // Whole-array replace — Firestore dot-notation cannot update array
        // elements by index; the only safe pattern is "send the whole array".
        patch: { strategicAngle: next },
      });
      if (!res.success) {
        const msg = res.error ?? 'Patch failed';
        setSavingError(msg);
        throw new Error(msg);
      }
    },
    [apiClient, projectId, strategyId, effectiveAuthorId],
  );

  // ─── action handlers ──────────────────────────────────────────────
  const handleSaveAngle = useCallback(
    async (next: StrategicAngleShape) => {
      const current = (strategy as any)?.strategicAngle as StrategicAngleShape[] | undefined;
      if (!current) return;
      const replaced = current.map((a) => (a.id === next.id ? next : a));
      await patchStrategicAngleArray(replaced);
    },
    [strategy, patchStrategicAngleArray],
  );

  const handlePolicyChange = useCallback(
    async (next: 'advisory' | 'strict') => {
      await patchScalar('audienceCoveragePolicy', next);
    },
    [patchScalar],
  );

  const handleGenerateTopicsForSprint = useCallback(
    async (sprintId: string, themeIds: string[], count?: number) => {
      if (!projectId || !strategyId || !currentUser) return;
      const res = await apiClient.generateTopicsForSprint(strategyId, sprintId, {
        projectId,
        userId: currentUser.uid,
        authorId: effectiveAuthorId,
        themeIds,
        count,
      });
      if (!res.success) {
        throw new Error(res.error ?? 'Failed to dispatch gap-fill task');
      }
    },
    [apiClient, projectId, strategyId, currentUser, effectiveAuthorId],
  );

  const handleRegenerate = useCallback(async () => {
    if (!projectId || !strategyId || !currentUser) return;
    setSavingError(null);
    setRegenerating(true);
    try {
      const res = await apiClient.regenerateStrategyDraft(strategyId, {
        projectId,
        authorId: effectiveAuthorId,
        userId: currentUser.uid,
      });
      if (!res.success) throw new Error(res.error ?? 'Regenerate failed');
    } catch (e) {
      setSavingError((e as Error).message);
    } finally {
      setRegenerating(false);
    }
  }, [apiClient, projectId, strategyId, currentUser, effectiveAuthorId]);

  const handleApprove = useCallback(async () => {
    if (!projectId || !strategyId || !currentUser) return;
    setApproveError(null);
    setApproveViolations([]);
    setApproving(true);
    try {
      const res = await apiClient.approveStrategy(strategyId, {
        projectId,
        userId: currentUser.uid,
        authorId: effectiveAuthorId,
        forceRestart: !isDraftPending,
        models: { strategy: approvalModel || projectDefaultModel.value },
      });
      if (!res.success) {
        // Server returned 422-style payload with violations inline
        const violations = ((res as any).violations as AudienceCoverageViolation[] | undefined)
          ?? Object.values(((res as any).violationsBySprintId ?? {}) as Record<string, AudienceCoverageViolation[]>)
            .flat();
        if (violations.length > 0) setApproveViolations(violations);
        throw new Error(res.error ?? 'Approval blocked');
      }
      // Success: B2 task dispatched. Navigate back to the content-plan page;
      // the task progress is observed via existing useBackendTask plumbing.
      navigate(`/project/${projectId}/content-plan?author=${encodeURIComponent(effectiveAuthorId)}&cpTab=strategies`);
    } catch (e) {
      setApproveError((e as Error).message);
    } finally {
      setApproving(false);
    }
  }, [
    apiClient,
    projectId,
    strategyId,
    currentUser,
    navigate,
    isDraftPending,
    effectiveAuthorId,
    approvalModel,
    projectDefaultModel.value,
  ]);

  // ─── route gates ──────────────────────────────────────────────────
  if (!projectId || !strategyId) {
    return <Navigate to="/all-projects" replace />;
  }
  if (!currentUser || (isContentPlanAdminOnly() && (!adminLoaded || contentPlanAccess.loading))) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading…</Typography>
      </Box>
    );
  }
  if (!canAccessContentPlan({ isAdmin, hasBillingAccess: contentPlanAccess.allowed })) return <Navigate to="/all-projects" replace />;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading draft…</Typography>
      </Box>
    );
  }
  if (!strategy) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Draft strategy not found.</Alert>
      </Box>
    );
  }

  const angles = ((strategy as any).strategicAngle as StrategicAngleShape[] | undefined) ?? [];
  const stageBadgeColor: 'warning' | 'success' | 'info' = isGenerated
    ? 'success'
    : isApproved
      ? 'info'
      : 'warning';

  const totalAssignments = sprints.reduce(
    (sum, sprint) => sum + (((sprint as any).assignments ?? []) as unknown[]).length,
    0,
  );
  const canRestartApprovedEmpty = isApproved && totalAssignments === 0;
  const approveDisabled = approving || (!isDraftPending && !canRestartApprovedEmpty);

  return (
    <Box
      sx={{ px: 3, py: 3, display: 'flex', gap: 3, alignItems: 'flex-start', maxWidth: 1440, mx: 'auto' }}
      data-testid={CONTENT_PLAN.STRATEGY_DRAFT_REVIEW_PAGE}
    >
      {/* Main body */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Header */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Tooltip title="Back to content plan">
              <IconButton
                size="small"
                onClick={() => navigate(`/project/${projectId}/content-plan`)}
                aria-label="back"
              >
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  {(strategy as any).name || strategy.id}
                </Typography>
                <Chip
                  size="small"
                  color={stageBadgeColor}
                  label={
                    isGenerated
                      ? 'generated'
                      : isApproved
                        ? 'approved'
                        : 'draft pending approval'
                  }
                />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {sprints.length} sprint{sprints.length === 1 ? '' : 's'}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                variant="outlined"
                size="small"
                startIcon={regenerating ? <CircularProgress size={14} /> : <RefreshIcon />}
                onClick={handleRegenerate}
                disabled={regenerating || !isDraftPending}
                data-testid={CONTENT_PLAN.STRATEGY_DRAFT_REGENERATE}
              >
                Regenerate draft
              </Button>
              <AvailableModelSelector
                projectId={projectId}
                value={approvalModel}
                onChange={handleApprovalModelChange}
                label="Generation model"
                disabled={approving || approveDisabled}
                sx={{ width: 300 }}
                testId={CONTENT_PLAN.STRATEGY_DRAFT_MODEL}
              />
              <Button
                variant="contained"
                size="small"
                color="primary"
                startIcon={approving ? <CircularProgress size={14} /> : <CheckCircleOutlineIcon />}
                onClick={handleApprove}
                disabled={approveDisabled}
                data-testid={CONTENT_PLAN.STRATEGY_DRAFT_APPROVE}
              >
                {isDraftPending ? 'Approve & generate' : 'Restart generation'}
              </Button>
            </Stack>
          </Stack>

          {savingError && (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              {savingError}
            </Alert>
          )}
          {approveError && (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              {approveError}
            </Alert>
          )}
          {approveViolations.length > 0 && (
            <Alert severity="warning" sx={{ mt: 1.5 }}>
              <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                Approval blocked under strict policy ({approveViolations.length} violation{approveViolations.length === 1 ? '' : 's'})
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Resolve the gaps below (each sprint flagged with offending audience ids) and retry.
              </Typography>
            </Alert>
          )}
        </Paper>

        {/* Strategic angles */}
        {angles.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Strategic angles
            </Typography>
            <Stack spacing={1.5}>
              {angles.map((angle, idx) => (
                <StrategicAngleCard
                  key={angle.id}
                  angle={angle}
                  index={idx}
                  onSave={handleSaveAngle}
                  disabled={!isDraftPending}
                />
              ))}
            </Stack>
          </Box>
        )}

        {/* Sprints */}
        <Box>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Sprints ({sprints.length})
          </Typography>
          <Stack spacing={1.5}>
            {sprints.map((sprint, idx) => (
              <SprintDraftBlock
                key={sprint.id}
                sprint={sprint}
                index={idx}
                availableAudienceBriefRefs={
                  ((strategy as any).primaryAudienceBriefRefs as any[] | undefined) ?? []
                }
                backlog={backlog ?? []}
                themeCatalog={themeCatalogMap}
                onPatchScalar={patchScalar}
                onGenerateTopics={handleGenerateTopicsForSprint}
                disabled={!isDraftPending}
              />
            ))}
          </Stack>
        </Box>
      </Box>

      {/* Right rail */}
      <Box sx={{ width: 320, flexShrink: 0 }}>
        <StrategyDraftDataUsagePanel
          strategy={strategy}
          sprints={sprints}
          backlog={backlog ?? []}
          themeCatalog={themeCatalogMap}
          onChangePolicy={handlePolicyChange}
          disabled={!isDraftPending}
        />
      </Box>
    </Box>
  );
};

export default StrategyDraftReviewPage;
